import type React from 'react';

import { withI18nPlaceholders } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';

import I18N_PLACEHOLDERS from '#config/i18n/placeholders/spikingPetitionBanner/spiking-petition-banner.yml';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { PetitionDetailsPageUserData } from '#src/app/pages/petitionDetails/pageContext';
import { TrackExperimentExposure } from '#src/app/shared/components/tracking';

import { SpikingPetitionBannerBar } from './components/SpikingPetitionBannerBar';
import { useBannerDismissal } from './hooks/useBannerDismissal';
import {
	useTrackSpikingPetitionBanner,
	useTrackSpikingPetitionBannerView,
} from './hooks/useTrackSpikingPetitionBanner';
import { useTreatSpikingPetitionBannerExperiment } from './hooks/useTreatSpikingPetitionBannerExperiment';

/** Renders the banner for `v1`, so its view, click and dismiss events fire only where it is visible. */
const SpikingPetitionBannerVariant: React.FC<ContentProps> = ({ experiment, petition, onDismissed }) => {
	const { signatureCountOverride, petitionTitleOverride, timeRangeOverride } = experiment.config;
	const { trackClick, trackDismiss } = useTrackSpikingPetitionBanner(petition.id);
	useTrackSpikingPetitionBannerView(petition.id);

	const handleClick = () => {
		trackClick();
		// Deferred: dismissing unmounts the link, which could cancel the navigation this click started.
		setTimeout(onDismissed, 0);
	};

	return (
		<SpikingPetitionBannerBar
			href={`/p/${petition.slug}`}
			signatureCount={signatureCountOverride ?? petition.signatureCount}
			petitionTitle={petitionTitleOverride || petition.displayTitle}
			timeRange={timeRangeOverride || undefined}
			onClick={handleClick}
			onClose={trackDismiss}
			onDismissed={onDismissed}
		/>
	);
};

/**
 * Relevance is decided before anything renders: `BannerSection` only mounts this when the
 * page-user-data normalizer returned banner data (experiment enabled, spiking petition published, not
 * its own page) and no higher-priority banner won. Nothing renders until the dismissal state has
 * loaded, or once the user dismissed this petition's banner. Only then is the user treated, and only
 * once the treatment is registered does exposure fire, for every arm, with `v1` rendering the banner.
 */
const SpikingPetitionBannerContent: React.FC<Props> = ({ experiment, petition }) => {
	const {
		data: {
			petition: { id: currentPetitionId },
		},
	} = usePetitionDetailsPageData();
	const { ready, dismissed, dismiss } = useBannerDismissal(petition.id);
	const treatedExperiment = useTreatSpikingPetitionBannerExperiment(experiment, { enabled: ready && !dismissed });

	if (!ready || dismissed || !treatedExperiment) return null;

	return (
		<>
			<TrackExperimentExposure experiment={treatedExperiment} resetKeys={[currentPetitionId]} />
			{treatedExperiment.variation === 'v1' && (
				<SpikingPetitionBannerVariant experiment={treatedExperiment} petition={petition} onDismissed={dismiss} />
			)}
		</>
	);
};

const SpikingPetitionBannerComponent: React.FC<Props> = ({ experiment, petition }) => (
	// Client-only: dismissal depends on cookie prefs and localStorage, and guest pages are server-cached.
	<ClientRender>
		<SpikingPetitionBannerContent experiment={experiment} petition={petition} />
	</ClientRender>
);

export const SpikingPetitionBanner = withI18nPlaceholders(I18N_PLACEHOLDERS, SpikingPetitionBannerComponent);

type Props = NonNullable<PetitionDetailsPageUserData['experiments']['spikingPetitionBanner']>;

type ContentProps = Props &
	Readonly<{
		onDismissed: () => void;
	}>;
